import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

// These will be initialized from data attributes

/**
 * A custom element that manages the bundle & save functionality.
 * This component handles quantity selection, subscription options, and variant selection.
 *
 * @typedef {object} BundleSaveRefs
 * @property {HTMLDivElement} quantityGrid - The quantity selection grid
 * @property {HTMLButtonElement[]} quantityOptions - The quantity option buttons
 * @property {HTMLDivElement} subscriptionContainer - The subscription selection container
 * @property {HTMLDivElement} selectColorsContainer - The select colors button container
 * @property {HTMLButtonElement} selectColorsButton - The select colors button
 * @property {HTMLElement} variantSelector - The variant quantity selector
 * @property {HTMLButtonElement} backButton - The back button
 * @property {HTMLButtonElement[]} variantAddButtons - The variant add buttons
 * @property {HTMLDivElement[]} quantityCounters - The quantity counters
 * @property {HTMLInputElement[]} quantityFields - The quantity input fields
 * @property {HTMLButtonElement[]} quantityMinusButtons - The quantity minus buttons
 * @property {HTMLButtonElement[]} quantityPlusButtons - The quantity plus buttons
 * @property {HTMLButtonElement} checkoutButton - The checkout button
 * @property {HTMLSpanElement} checkoutLoader - The checkout loader
 * @property {HTMLInputElement} variantId - The variant ID input
 * @property {HTMLDivElement} liveRegion - The live region for accessibility
 * @property {HTMLParagraphElement} recommendationText - The recommendation text paragraph
 * @extends Component<BundleSaveRefs>
 */
export class BundleSaveComponent extends Component {
  requiredRefs = [
    'quantityGrid',
    'subscriptionContainer',
    'selectColorsContainer',
    'selectColorsButton',
    'variantSelector',
    'checkoutButton',
    'checkoutContainer',
    'stickyButtons',
    'stickySelectButton',
    'stickyCheckoutButton',
    'stickyCheckoutLoader',
    'oneTimeLinkWrapper',
    'recommendationText'
  ];

  /** @type {number} */
  #selectedQuantity = 3;

  /** @type {boolean} */
  #isSubscription = true;

  /** @type {Map<string, number>} */
  #variantQuantities = new Map();

  /** @type {string | null} */
  #discountCode = null;

  /** @type {Map<HTMLElement, string> | null} */
  #originalDisplayStates = null;

  /** @type {(() => void) | null} */
  #resizeHandler = null;

  /** @type {boolean} */
  #hasFiredMarketingEvent = false;

  /**
   * Configuration from data attributes
   * @type {{
   *   sellingPlanId: string,
   *   discountCode: string,
   *   subscriptionDiscountPercent: number,
   *   gifts: {
   *     ebookVariantId: string,
   *     bottleVariantId: string,
   *     mysteryVariantId: string
   *   },
   *   pricing: {
   *     maxBagPrice: number,
   *     quantity1Price: number,
   *     quantity2Price: number,
   *     quantity3Price: number
   *   },
   *   productVariants: Array<{id: string, price: number, [key: string]: any}>,
   *   productPriceMin: number
   * }}
   */
  #config = {
    sellingPlanId: '5110399158',
    discountCode: 'WELCOME',
    subscriptionDiscountPercent: 25,
    gifts: {
      ebookVariantId: '44629507899574',
      bottleVariantId: '44505312493750',
      mysteryVariantId: '44629518287030'
    },
    pricing: {
      maxBagPrice: 45.95,
      quantity1Price: 41.95,
      quantity2Price: 34.95,
      quantity3Price: 32.95
    },
    productVariants: [],
    productPriceMin: 0
  };

  connectedCallback() {
    console.log('🚀 BundleSaveComponent connectedCallback started');
    console.log('🌐 User Agent:', navigator.userAgent);
    console.log('🍎 Is Safari:', /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent));
    console.log('📱 Is iOS:', /iPad|iPhone|iPod/.test(navigator.userAgent));
    console.log('🔧 Fetch API available:', typeof fetch !== 'undefined');
    console.log('🔧 Promise available:', typeof Promise !== 'undefined');
    console.log('🔧 Custom Elements available:', typeof customElements !== 'undefined');
    
    super.connectedCallback();

    // Initialize configuration from data attributes
    this.#initializeConfig();

    this.#initializeQuantitySelection();
    this.#initializeSubscriptionSelection();
    this.#initializeVariantSelection();
    this.#initializeCheckout();
    this.#initializeStickyButtons();
    this.#updateAllPricingDisplays();
    this.#updateGiftUnlocks();
    
    // Add resize listener to handle screen size changes
    this.#resizeHandler = () => {
      this.#updateStickyButtonsVisibility();
    };
    window.addEventListener('resize', this.#resizeHandler);
    
    console.log('✅ BundleSaveComponent initialization complete');
  }

  /**
   * Initialize configuration from data attributes
   */
  #initializeConfig() {
    // Selling plan ID
    this.#config.sellingPlanId = this.dataset.sellingPlanId || '5110399158';
    
    // Discount code
    this.#config.discountCode = this.dataset.discountCode || 'WELCOME';
    this.#discountCode = this.#config.discountCode;
    
    // Subscription discount percentage
    const subscriptionDiscount = parseFloat(this.dataset.subscriptionDiscount || '25');
    this.#config.subscriptionDiscountPercent = isNaN(subscriptionDiscount) ? 25 : subscriptionDiscount;
    
    // Gift variant IDs
    this.#config.gifts.ebookVariantId = this.dataset.giftEbookVariantId || '44629507899574';
    this.#config.gifts.bottleVariantId = this.dataset.giftBottleVariantId || '44505312493750';
    this.#config.gifts.mysteryVariantId = this.dataset.giftMysteryVariantId || '44629518287030';
    
    // Pricing fallbacks
    const maxBagPrice = parseFloat(this.dataset.maxBagPrice || '45.95');
    this.#config.pricing.maxBagPrice = isNaN(maxBagPrice) ? 45.95 : maxBagPrice;
    
    const q1Price = parseFloat(this.dataset.quantity1Price || '41.95');
    this.#config.pricing.quantity1Price = isNaN(q1Price) ? 41.95 : q1Price;
    
    const q2Price = parseFloat(this.dataset.quantity2Price || '34.95');
    this.#config.pricing.quantity2Price = isNaN(q2Price) ? 34.95 : q2Price;
    
    const q3Price = parseFloat(this.dataset.quantity3Price || '32.95');
    this.#config.pricing.quantity3Price = isNaN(q3Price) ? 32.95 : q3Price;
    
    // Product variants and pricing
    try {
      const variantsJson = this.dataset.productVariants;
      if (variantsJson) {
        this.#config.productVariants = JSON.parse(decodeURIComponent(variantsJson));
      }
    } catch (e) {
      console.warn('Failed to parse product variants:', e);
      this.#config.productVariants = [];
    }
    
    const productPriceMin = parseFloat(this.dataset.productPriceMin || '0');
    this.#config.productPriceMin = isNaN(productPriceMin) ? 0 : productPriceMin / 100; // Convert from cents to dollars
    
    console.log('⚙️ Configuration initialized:', this.#config);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Remove resize listener
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
    }
  }

  /**
   * Initialize quantity selection functionality
   */
  #initializeQuantitySelection() {
    if (!this.refs.quantityOptions) return;

    // Set initial quantity from the pre-selected option
    const initiallySelected = Array.from(this.refs.quantityOptions).find(
      option => option.getAttribute('aria-selected') === 'true'
    );
    if (initiallySelected) {
      const qtyAttr = initiallySelected.getAttribute('data-offer-qty') || '3';
      this.#selectedQuantity = parseInt(qtyAttr, 10);
      console.log('📊 Initial quantity from pre-selected option:', this.#selectedQuantity);
    }

    this.refs.quantityOptions.forEach((option) => {
      option.addEventListener('click', (event) => {
        // Find the quantity option element (either the clicked element or its parent)
        const target = event.target;
        if (target && target instanceof Element) {
          const quantityOption = target.closest('.bundle-save__quantity-option') || target;
          if (quantityOption) this.#handleQuantitySelection(quantityOption);
        }
      });
    });
  }

  /**
   * Handle quantity selection
   * @param {Element} selectedOption - The selected quantity option
   */
  #handleQuantitySelection(selectedOption) {
    // Update aria-selected attributes
    this.refs.quantityOptions.forEach(option => {
      option.setAttribute('aria-selected', 'false');
    });
    selectedOption.setAttribute('aria-selected', 'true');

    // Update selected quantity
    const qtyAttr = selectedOption.getAttribute('data-offer-qty') || '3';
    this.#selectedQuantity = parseInt(qtyAttr, 10);

    // Update pricing displays
    this.#updateAllPricingDisplays();
    this.#updateGiftUnlocks();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('quantity-selected', {
      detail: { selectedQuantity: this.#selectedQuantity },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Initialize subscription selection functionality
   */
  #initializeSubscriptionSelection() {
    // Force subscription-only: preselect subscription, hide/disable one-time option and related pricing
    this.#isSubscription = true;

    const subscribeInput = this.querySelector('input[name^="subscription-type-"][value="subscribe"]');
    if (subscribeInput && subscribeInput instanceof HTMLInputElement) {
      subscribeInput.checked = true;
    }

    // Reset label styles
    const allLabels = this.querySelectorAll('.bundle-save__subscription-label');
    allLabels.forEach(label => {
      if (label instanceof HTMLElement) {
        label.style.borderColor = 'var(--color-border)';
        label.style.background = 'var(--color-background)';
      }
    });

    // Emphasize subscription selection
    const selectedLabel = subscribeInput?.closest('.bundle-save__subscription-option')?.querySelector('.bundle-save__subscription-label');
    if (selectedLabel && selectedLabel instanceof HTMLElement) {
      selectedLabel.style.borderColor = '#16a34a';
      selectedLabel.style.background = '#f7ffe3';
    }

    // Hide/disable non-subscription options
    const subscriptionOptions = this.querySelectorAll('input[name^="subscription-type-"]');
    subscriptionOptions.forEach(option => {
      if (option && option instanceof HTMLInputElement) {
        if (option.value !== 'subscribe') {
          option.checked = false;
          option.disabled = true;
          const optionEl = option.closest('.bundle-save__subscription-option');
          if (optionEl && optionEl instanceof HTMLElement) optionEl.style.display = 'none';
        } else {
          option.disabled = false;
        }
      }
    });

    // Hide any one-time pricing comparison blocks if present
    const oneTimePricingEls = this.querySelectorAll('[data-price-comparison="one-time"]');
    oneTimePricingEls.forEach(el => {
      if (el && el instanceof HTMLElement) el.style.display = 'none';
    });

    // Update UI to reflect forced subscription selection
    this.#updateAllPricingDisplays();
    this.#updateGiftUnlocks();
  }

  /**
   * Handle subscription selection change
   * @param {HTMLInputElement} selectedOption - The selected subscription option
   */
  #handleSubscriptionChange(selectedOption) {
    if (!(selectedOption instanceof HTMLInputElement)) return;
    this.#isSubscription = selectedOption.value === 'subscribe';
    
    // Update UI to reflect selection - use exact CSS colors
    const allLabels = this.querySelectorAll('.bundle-save__subscription-label');
    allLabels.forEach(label => {
      if (label instanceof HTMLElement) {
        label.style.borderColor = 'var(--color-border)';
        label.style.background = 'var(--color-background)';
      }
    });
    
    const selectedLabel = selectedOption.closest('.bundle-save__subscription-option')?.querySelector('.bundle-save__subscription-label');
    if (selectedLabel && selectedLabel instanceof HTMLElement) {
      selectedLabel.style.borderColor = '#16a34a'; // Match CSS: border-color: #16a34a
      selectedLabel.style.background = '#f7ffe3'; // Match CSS: background: #f7ffe3
    }

    // Update all pricing displays
    this.#updateAllPricingDisplays();
    this.#updateGiftUnlocks();
  }

  /**
   * Initialize variant selection functionality
   */
  #initializeVariantSelection() {
    if (!this.refs.selectColorsButton || !this.refs.variantSelector) return;

    this.refs.selectColorsButton.addEventListener('click', () => {
      this.#showVariantSelector();
    });

    if (this.refs.backButton) {
      this.refs.backButton.addEventListener('click', () => {
        this.#hideVariantSelector();
      });
    }

    // Initialize variant add buttons
    if (this.refs.variantAddButtons) {
      this.refs.variantAddButtons.forEach((button) => {
        const isAvailable = button.getAttribute('data-available') !== 'false';
        const inventoryPolicy = button.getAttribute('data-inventory-policy');
        const canAddToCart = isAvailable || inventoryPolicy === 'continue';
        
        if (!canAddToCart) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          button.textContent = 'Sold out';
          return;
        }
        button.addEventListener('click', (event) => {
          const t = event.target;
          if (t && t instanceof HTMLButtonElement) this.#showQuantitySelector(t);
        });
      });
    }

    // Initialize quantity controls
    if (this.refs.quantityPlusButtons) {
      this.refs.quantityPlusButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          const t = event.target;
          if (t && t instanceof HTMLButtonElement) this.#incrementQuantity(t);
        });
      });
    }

    if (this.refs.quantityMinusButtons) {
      this.refs.quantityMinusButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          const t = event.target;
          if (t && t instanceof HTMLButtonElement) this.#decrementQuantity(t);
        });
      });
    }

    if (this.refs.quantityFields) {
      this.refs.quantityFields.forEach((field) => {
        field.addEventListener('change', (event) => {
          const t = event.target;
          if (t && t instanceof HTMLInputElement) this.#onQuantityChange(t);
        });
      });
    }
  }

  /**
   * Show the variant selector
   */
  #showVariantSelector() {
    if (!this.refs.variantSelector || !this.refs.selectColorsContainer || !this.refs.quantityGrid || !this.refs.subscriptionContainer) return;

    // Reset all quantity inputs
    this.#resetAllQuantityInputs();
    
    // Show variant selector
    this.refs.variantSelector.style.display = 'block';
    
    // Hide other elements
    this.refs.selectColorsContainer.style.display = 'none';
    this.refs.quantityGrid.style.display = 'none';
    this.refs.subscriptionContainer.style.display = 'none';

    // Hide the "try it once no autoship" button
    if (this.refs.oneTimeLinkWrapper && this.refs.oneTimeLinkWrapper instanceof HTMLElement) {
      this.refs.oneTimeLinkWrapper.style.display = 'none';
    }

    // Hide the recommendation text
    if (this.refs.recommendationText && this.refs.recommendationText instanceof HTMLElement) {
      this.refs.recommendationText.style.display = 'none';
    }

    // Hide all page content except countdown bar and gallery (desktop only)
    this.#hidePageContentForColorSelection();

    // Update checkout button state
    this.#updateCheckoutButton();

    // Force show sticky buttons when entering variant selector (second step)
    // This ensures the sticky checkout button is visible regardless of scroll position
    if (this.refs.stickyButtons instanceof HTMLElement) {
      this.refs.stickyButtons.style.display = 'block';
      this.refs.stickyButtons.classList.remove('hidden');
      this.refs.stickyButtons.classList.add('is-visible');
      document.body.classList.add('sticky-button-active');
    }

    // Update sticky buttons visibility and ensure checkout button is shown on second step
    this.#updateStickyButtonsVisibility();

    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Hide the variant selector
   */
  #hideVariantSelector() {
    if (!this.refs.variantSelector || !this.refs.selectColorsContainer || !this.refs.quantityGrid || !this.refs.subscriptionContainer) return;

    // Hide variant selector
    this.refs.variantSelector.style.display = 'none';
    
    // Show other elements
    this.refs.selectColorsContainer.style.display = 'block';
    this.refs.quantityGrid.style.display = 'flex';
    this.refs.subscriptionContainer.style.display = 'flex';

    // Restore the "try it once no autoship" button
    if (this.refs.oneTimeLinkWrapper && this.refs.oneTimeLinkWrapper instanceof HTMLElement) {
      this.refs.oneTimeLinkWrapper.style.display = 'block';
    }

    // Restore the recommendation text
    if (this.refs.recommendationText && this.refs.recommendationText instanceof HTMLElement) {
      this.refs.recommendationText.style.display = 'block';
    }

    // Restore all page content
    this.#restorePageContentFromColorSelection();

    // Update sticky buttons visibility
    this.#updateStickyButtonsVisibility();
  }

  /**
   * Show quantity selector for a variant
   * @param {HTMLButtonElement} addButton - The add button that was clicked
   */
  #showQuantitySelector(addButton) {
    if (addButton.hasAttribute('disabled')) return;
    const addContainer = addButton.closest('.bundle-save__variant-add');
    if (!addContainer) return;
    const qtyCounter = addContainer.querySelector('.bundle-save__quantity-counter');
    const qtyInput = addContainer.querySelector('.bundle-save__quantity-field');
    const addBtn = addContainer.querySelector('.bundle-save__variant-add-button');

    if (qtyCounter && qtyInput instanceof HTMLInputElement && addBtn) {
      addBtn.classList.add('hidden');
      qtyCounter.classList.remove('hidden');
      qtyInput.value = '1';
      qtyInput.focus();
      
      // Fire Facebook tracking event only once per page visit
      this.#fireMarketingAddToCartEvent(addButton);
      
      this.#updateVariantQuantities();
      this.#updateCheckoutButton();
      this.#updateGiftUnlocks();
    }
  }

  /**
   * Fire both Facebook marketing and Shopify analytics add to cart events (only once per page visit)
   * @param {HTMLButtonElement} addButton - The add button that was clicked
   */
  #fireMarketingAddToCartEvent(addButton) {
    // Only fire the event once per page visit
    if (this.#hasFiredMarketingEvent) {
      return;
    }

    const variantId = addButton.getAttribute('data-variant-id');
    const productId = this.dataset.productId || 'bundle-save-product';
    
    if (variantId) {
      // Mark that we've fired the event for this page visit
      this.#hasFiredMarketingEvent = true;
      
      // 1. Dispatch the marketing add to cart event for Facebook tracking
      const marketingEvent = new CustomEvent('marketing:add-to-cart', {
        detail: {
          productId,
          variantId,
          data: {
            quantity: 1,
            source: 'bundle-save-component',
            timestamp: Date.now(),
          },
        },
        bubbles: true
      });
      this.dispatchEvent(marketingEvent);
      
      // 2. Dispatch Shopify analytics CartAddEvent for proper analytics tracking
      const cartAddEvent = new CartAddEvent({}, variantId, {
        source: 'bundle-save-component',
        itemCount: 1,
        productId: productId,
        variantId: variantId,
        sections: {} // Bundle save doesn't update sections, so empty object
      });
      this.dispatchEvent(cartAddEvent);
      
      console.log('📊 Both Facebook and Shopify analytics AddToCart events fired for bundle-save:', { productId, variantId });
    }
  }

  /**
   * Increment quantity for a variant
   * @param {HTMLButtonElement} plusButton - The plus button that was clicked
   */
  #incrementQuantity(plusButton) {
    const container = plusButton.closest('.bundle-save__quantity-counter');
    const qtyInput = container ? container.querySelector('.bundle-save__quantity-field') : null;
    if (qtyInput && qtyInput instanceof HTMLInputElement) {
      qtyInput.value = String(parseInt(qtyInput.value) + 1);
      this.#checkQuantity(qtyInput);
      this.#updateVariantQuantities();
      this.#updateCheckoutButton();
      this.#updateGiftUnlocks();
    }
  }

  /**
   * Decrement quantity for a variant
   * @param {HTMLButtonElement} minusButton - The minus button that was clicked
   */
  #decrementQuantity(minusButton) {
    const container = minusButton.closest('.bundle-save__quantity-counter');
    const qtyInput = container ? container.querySelector('.bundle-save__quantity-field') : null;
    if (qtyInput && qtyInput instanceof HTMLInputElement && parseInt(qtyInput.value) > 1) {
      qtyInput.value = String(parseInt(qtyInput.value) - 1);
      this.#checkQuantity(qtyInput);
      this.#updateVariantQuantities();
      this.#updateCheckoutButton();
      this.#updateGiftUnlocks();
    } else if (qtyInput && qtyInput instanceof HTMLInputElement && parseInt(qtyInput.value) <= 1) {
      // Hide quantity counter and show add button
      const addContainer = minusButton.closest('.bundle-save__variant-add');
      if (!addContainer) return;
      const qtyCounter = addContainer.querySelector('.bundle-save__quantity-counter');
      const addBtn = addContainer.querySelector('.bundle-save__variant-add-button');
      
      if (qtyCounter && addBtn) {
        qtyCounter.classList.add('hidden');
        addBtn.classList.remove('hidden');
        qtyInput.value = '0';
      }
      
      this.#updateVariantQuantities();
      this.#updateCheckoutButton();
      this.#updateGiftUnlocks();
    }
  }

  /**
   * Handle quantity input change
   * @param {HTMLInputElement} qtyInput - The quantity input that changed
   */
  #onQuantityChange(qtyInput) {
    this.#checkQuantity(qtyInput);
    this.#updateVariantQuantities();
    this.#updateCheckoutButton();
    this.#updateGiftUnlocks();
  }

  /**
   * Check and validate quantity input
   * @param {HTMLInputElement} qtyInput - The quantity input to check
   */
  #checkQuantity(qtyInput) {
    const value = parseInt(qtyInput.value);
    if (value < 0) {
      qtyInput.value = '0';
    }
  }

  /**
   * Update variant quantities map
   */
  #updateVariantQuantities() {
    this.#variantQuantities.clear();
    
    if (this.refs.quantityFields) {
      this.refs.quantityFields.forEach((field) => {
        const variantId = field.getAttribute('data-variant-id');
        const quantity = parseInt(field.value) || 0;
        if (quantity > 0) {
          this.#variantQuantities.set(String(variantId), quantity);
        }
      });
    }
  }

  /**
   * Get total quantity across all variants
   * @returns {number} Total quantity
   */
  #getTotalQuantity() {
    let total = 0;
    this.#variantQuantities.forEach(quantity => {
      total += quantity;
    });
    return total;
  }

  /**
   * Update checkout button state
   */
  #updateCheckoutButton() {
    if (!this.refs.checkoutButton) return;

    const totalQuantity = this.#getTotalQuantity();
    const bottlesToAdd = this.#selectedQuantity - totalQuantity;

    if (bottlesToAdd <= 0) {
      this.refs.checkoutButton.removeAttribute('disabled');
      const checkoutText = this.querySelector('[data-checkout-text]');
      if (checkoutText) {
        checkoutText.textContent = 'Proceed to Checkout';
      }
      
      // Sync sticky checkout button
      if (this.refs.stickyCheckoutButton && this.refs.stickyCheckoutButton instanceof HTMLElement) {
        this.refs.stickyCheckoutButton.removeAttribute('disabled');
        const stickyCheckoutText = this.querySelector('[data-sticky-checkout-text]');
        if (stickyCheckoutText) {
          stickyCheckoutText.textContent = 'Proceed to Checkout';
        }
      }
    } else {
      this.refs.checkoutButton.setAttribute('disabled', 'disabled');
      const checkoutText = this.querySelector('[data-checkout-text]');
      if (checkoutText) {
        checkoutText.textContent = `Select ${bottlesToAdd} more`;
      }
      
      // Sync sticky checkout button
      if (this.refs.stickyCheckoutButton && this.refs.stickyCheckoutButton instanceof HTMLElement) {
        this.refs.stickyCheckoutButton.setAttribute('disabled', 'disabled');
        const stickyCheckoutText = this.querySelector('[data-sticky-checkout-text]');
        if (stickyCheckoutText) {
          stickyCheckoutText.textContent = `Select ${bottlesToAdd} more`;
        }
      }
    }
  }

  /**
   * Reset all quantity inputs
   */
  #resetAllQuantityInputs() {
    if (!this.refs.quantityFields) return;

    this.refs.quantityFields.forEach(input => {
      input.value = '0';
      
      // Reset UI state
      const addContainer = input.closest('.bundle-save__variant-add');
      if (!addContainer) return;
      const qtyCounter = addContainer.querySelector('.bundle-save__quantity-counter');
      const addBtn = addContainer.querySelector('.bundle-save__variant-add-button');
      
      if (qtyCounter && addBtn) {
        qtyCounter.classList.add('hidden');
        addBtn.classList.remove('hidden');
      }
    });
    
    this.#variantQuantities.clear();
    this.#updateCheckoutButton();
    this.#updateGiftUnlocks();
  }

  /**
   * Initialize checkout functionality
   */
  #initializeCheckout() {
    if (!this.refs.checkoutButton) return;

    this.refs.checkoutButton.addEventListener('click', (event) => {
      this.#handleCheckout(event);
    });
  }

  /**
   * Initialize sticky mobile buttons functionality
   */
  #initializeStickyButtons() {
    console.log('🔍 Initializing sticky buttons...');
    console.log('🔍 Sticky buttons ref:', this.refs.stickyButtons);
    
    if (!this.refs.stickyButtons) {
      console.log('❌ No sticky buttons ref found');
      return;
    }

    console.log('✅ Sticky buttons ref found, setting up scroll listener');

    // Add scroll listener to show sticky select button only after scrolling past original select button
    let hasScrolledPastSelectButton = false;
    const handleScroll = () => {
      console.log('📜 Scroll detected, scrollY:', window.scrollY);
      
      // Check if we've scrolled past the original select colors button
      if (!hasScrolledPastSelectButton && this.refs.selectColorsContainer) {
        const selectButtonRect = this.refs.selectColorsContainer.getBoundingClientRect();
        const scrollThreshold = window.scrollY + selectButtonRect.bottom;
        
        console.log('🔍 Select button bottom:', selectButtonRect.bottom, 'Scroll threshold:', scrollThreshold);
        
        if (window.scrollY > scrollThreshold) {
          hasScrolledPastSelectButton = true;
          console.log('✅ Scrolled past select button, showing sticky buttons');
          this.#showStickyButtons();
          this.#setupMainButtonVisibilityObserver();
          window.removeEventListener('scroll', handleScroll);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);

    // Initialize sticky select button
    console.log('🔍 Sticky select button ref:', this.refs.stickySelectButton);
    if (this.refs.stickySelectButton && this.refs.stickySelectButton instanceof HTMLElement) {
      console.log('✅ Setting up sticky select button event listener');
      this.refs.stickySelectButton.addEventListener('click', () => {
        console.log('🖱️ Sticky select button clicked');
        this.#showVariantSelector();
      });
    } else {
      console.log('❌ Sticky select button ref not found or not HTMLElement');
    }

    // Initialize sticky checkout button
    console.log('🔍 Sticky checkout button ref:', this.refs.stickyCheckoutButton);
    if (this.refs.stickyCheckoutButton && this.refs.stickyCheckoutButton instanceof HTMLElement) {
      console.log('✅ Setting up sticky checkout button event listener');
      this.refs.stickyCheckoutButton.addEventListener('click', (event) => {
        console.log('🖱️ Sticky checkout button clicked');
        this.#handleCheckout(event);
      });
    } else {
      console.log('❌ Sticky checkout button ref not found or not HTMLElement');
    }


    // Update sticky buttons visibility based on current state
    console.log('🔍 Updating sticky buttons visibility');
    this.#updateStickyButtonsVisibility();

    // Sync pricing data to sticky button
    this.#syncStickyButtonPricing();

    // Debug: Add a test button to force show sticky buttons
    if (typeof window !== 'undefined') {
      /** @type {any} */ (window).testShowStickyButtons = () => {
        console.log('🧪 Test: Force showing sticky buttons');
        this.#showStickyButtons();
      };
      console.log('🧪 Debug: Call window.testShowStickyButtons() to force show sticky buttons');
    }
  }

  /**
   * Show sticky buttons with animation
   */
  #showStickyButtons() {
    console.log('🎬 Showing sticky buttons...');
    console.log('🔍 Sticky buttons element:', this.refs.stickyButtons);
    
    if (!this.refs.stickyButtons || !(this.refs.stickyButtons instanceof HTMLElement)) {
      console.log('❌ Cannot show sticky buttons - element not found or not HTMLElement');
      return;
    }
    
    console.log('✅ Removing hidden class from sticky buttons');
    this.refs.stickyButtons.classList.remove('hidden');
    this.refs.stickyButtons.classList.add('is-visible');
    
    // Add class to body to indicate sticky button is active
    document.body.classList.add('sticky-button-active');
    
    console.log('🎉 Sticky buttons should now be visible');
  }

  /**
   * Setup intersection observer to detect when main button is visible
   */
  #setupMainButtonVisibilityObserver() {
    console.log('🔍 Setting up main button visibility observer...');
    
    if (!this.refs.selectColorsContainer || !this.refs.stickyButtons) {
      console.log('❌ Missing refs for visibility observer');
      return;
    }

    const mainButtonContainer = this.refs.selectColorsContainer instanceof HTMLElement ? this.refs.selectColorsContainer : null;
    const stickyButtons = this.refs.stickyButtons instanceof HTMLElement ? this.refs.stickyButtons : null;
    
    if (!mainButtonContainer || !stickyButtons) {
      console.log('❌ Main button container or sticky buttons not found');
      return;
    }

    // Create intersection observer to watch when main button is visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        console.log('👁️ Main button visibility changed:', entry.isIntersecting);
        if (entry.isIntersecting) {
          // Main select button is visible, hide sticky buttons
          console.log('✅ Main select button visible, hiding sticky buttons');
          stickyButtons.style.display = 'none';
          document.body.classList.remove('sticky-button-active');
          // Ensure main checkout button is visible when select button is visible
          if (this.refs.checkoutContainer instanceof HTMLElement) {
            this.refs.checkoutContainer.style.display = 'block';
          }
        } else {
          // Main select button is not visible, show sticky buttons
          console.log('✅ Main select button not visible, showing sticky buttons');
          stickyButtons.style.display = 'block';
          document.body.classList.add('sticky-button-active');
          // Hide main checkout button when using sticky button
          if (this.refs.checkoutContainer instanceof HTMLElement) {
            this.refs.checkoutContainer.style.display = 'none';
          }
        }
      });
    }, {
      threshold: 0.1, // Trigger when 10% of the element is visible
      rootMargin: '0px 0px -100px 0px' // Add some margin to account for sticky positioning
    });

    observer.observe(mainButtonContainer);
    console.log('✅ Intersection observer set up for main button');
  }

  /**
   * Sync pricing data from main button to sticky button
   */
  #syncStickyButtonPricing() {
    console.log('💰 Syncing sticky button pricing...');
    
    if (!this.refs.stickySelectButton || !(this.refs.stickySelectButton instanceof HTMLElement)) {
      console.log('❌ Sticky select button not found for pricing sync');
      return;
    }

    // Find the main button's price comparison elements
    const mainPriceComparison = this.querySelector('[data-price-comparison]');
    const stickyPriceComparison = this.refs.stickySelectButton.querySelector('[data-price-comparison]');
    
    if (mainPriceComparison && stickyPriceComparison) {
      const mainOriginalPrice = mainPriceComparison.querySelector('.bundle-save__original-price');
      const mainDiscountedPrice = mainPriceComparison.querySelector('.bundle-save__discounted-price');
      
      const stickyOriginalPrice = stickyPriceComparison.querySelector('.bundle-save__original-price');
      const stickyDiscountedPrice = stickyPriceComparison.querySelector('.bundle-save__discounted-price');
      
      if (mainOriginalPrice && stickyOriginalPrice) {
        stickyOriginalPrice.textContent = mainOriginalPrice.textContent;
        stickyOriginalPrice.classList.toggle('hidden', mainOriginalPrice.classList.contains('hidden'));
      }
      
      if (mainDiscountedPrice && stickyDiscountedPrice) {
        stickyDiscountedPrice.textContent = mainDiscountedPrice.textContent;
      }
      
      console.log('✅ Pricing synced to sticky button');
    }
  }

  /**
   * Update sticky buttons visibility based on current state
   */
  #updateStickyButtonsVisibility() {
    console.log('🔄 Updating sticky buttons visibility...');
    
    if (!this.refs.stickySelectButton) {
      console.log('❌ Missing sticky select button ref');
      return;
    }

    const stickySelectButton = this.refs.stickySelectButton instanceof HTMLElement ? this.refs.stickySelectButton : null;
    
    if (!stickySelectButton) {
      console.log('❌ Sticky select button is not HTMLElement');
      return;
    }

    const isVariantSelectorVisible = this.refs.variantSelector && 
      this.refs.variantSelector instanceof HTMLElement &&
      this.refs.variantSelector.style.display !== 'none';

    // Check if we're on mobile (screen width <= 768px)
    const isMobile = window.innerWidth <= 768;
    
    console.log('🔍 Variant selector visible:', isVariantSelectorVisible);
    console.log('🔍 Variant selector display:', this.refs.variantSelector?.style?.display);
    console.log('📱 Is mobile:', isMobile);
    
    if (isVariantSelectorVisible) {
      // Show checkout button when in variant selector mode (second step)
      // Always show sticky checkout button on second step regardless of scroll position
      console.log('✅ Showing checkout button (variant selector mode - second step)');
      
      // Ensure sticky buttons container is visible
      if (this.refs.stickyButtons instanceof HTMLElement) {
        this.refs.stickyButtons.style.display = 'block';
        this.refs.stickyButtons.classList.remove('hidden');
        this.refs.stickyButtons.classList.add('is-visible');
        document.body.classList.add('sticky-button-active');
      }
      
      // Hide select button and show checkout button
      stickySelectButton.classList.add('hidden');
      if (this.refs.stickyCheckoutButton instanceof HTMLElement) {
        this.refs.stickyCheckoutButton.classList.remove('hidden');
      }
      
      // Hide main checkout button only on mobile when in variant selector mode
      if (this.refs.checkoutContainer instanceof HTMLElement) {
        this.refs.checkoutContainer.style.display = isMobile ? 'none' : 'block';
      }
    } else {
      // Show select button when not in variant selector mode (first step)
      // Only show sticky select button if we've scrolled past the original button
      console.log('✅ First step - checking if should show sticky select button');
      
      // Hide checkout button and show select button
      stickySelectButton.classList.remove('hidden');
      if (this.refs.stickyCheckoutButton instanceof HTMLElement) {
        this.refs.stickyCheckoutButton.classList.add('hidden');
      }
      
      // Check if we should show sticky buttons based on scroll position
      if (this.refs.selectColorsContainer) {
        const selectButtonRect = this.refs.selectColorsContainer.getBoundingClientRect();
        const scrollThreshold = window.scrollY + selectButtonRect.bottom;
        
        if (window.scrollY > scrollThreshold) {
          // We've scrolled past the original select button, show sticky buttons
          if (this.refs.stickyButtons instanceof HTMLElement) {
            this.refs.stickyButtons.style.display = 'block';
            this.refs.stickyButtons.classList.remove('hidden');
            this.refs.stickyButtons.classList.add('is-visible');
            document.body.classList.add('sticky-button-active');
          }
        } else {
          // We haven't scrolled past the original select button yet, hide sticky buttons
          if (this.refs.stickyButtons instanceof HTMLElement) {
            this.refs.stickyButtons.style.display = 'none';
            this.refs.stickyButtons.classList.add('hidden');
            document.body.classList.remove('sticky-button-active');
          }
        }
      }
      
      // Always show main checkout button when not in variant selector mode
      if (this.refs.checkoutContainer instanceof HTMLElement) {
        this.refs.checkoutContainer.style.display = 'block';
      }
    }
  }

  /**
   * Handle checkout process
   * @param {MouseEvent} event - The click event
   */
  #handleCheckout(event) {
    console.log('🛒 Starting checkout process...');
    
    event.preventDefault();
    
    if (this.#getTotalQuantity() < this.#selectedQuantity) {
      console.log('❌ Not enough items selected. Total:', this.#getTotalQuantity(), 'Required:', this.#selectedQuantity);
      return; // Not enough items selected
    }

    console.log('✅ Quantity check passed. Total quantity:', this.#getTotalQuantity());

    // Show loading state
    this.#showLoadingState();
    
    // Build cart items
    /** @type {{id: string, quantity: number, selling_plan?: string}[]} */
    const cartItems = [];
    this.#variantQuantities.forEach((quantity, variantId) => {
      cartItems.push({
        id: String(variantId),
        quantity: quantity
      });
    });

    console.log('📦 Cart items before subscription:', cartItems);

    // Add subscription selling plan if needed
    if (this.#isSubscription) {
      console.log('🔄 Adding subscription selling plan...');
      cartItems.forEach(item => {
        item.selling_plan = this.#config.sellingPlanId;
      });
      console.log('✅ Subscription selling plan added:', this.#config.sellingPlanId);
    }

    // Add free gifts based on unlocked state (shipping is not a product)
    const unlocked = this.#getUnlockedGiftKeys();
    console.log('🎁 Unlocked gifts:', Array.from(unlocked));
    const giftsToAdd = [];
    if (unlocked.has('ebook')) giftsToAdd.push(this.#config.gifts.ebookVariantId);
    if (unlocked.has('bottle')) giftsToAdd.push(this.#config.gifts.bottleVariantId);
    if (unlocked.has('mystery')) giftsToAdd.push(this.#config.gifts.mysteryVariantId);

    console.log('🎁 Gifts to add:', giftsToAdd);

    giftsToAdd.forEach((giftId) => {
      const normalizedId = this.#normalizeVariantId(giftId);
      console.log('🎁 Adding gift:', giftId, '-> normalized:', normalizedId);
      // Only one set of gifts per order
      cartItems.push({ id: normalizedId, quantity: 1 });
    });

    // Ensure discount code is applied
    this.#discountCode = this.#config.discountCode;
    console.log('💰 Discount code set:', this.#discountCode);

    console.log('📦 Final cart items:', cartItems);

    // Redirect to checkout
    this.#redirectToCheckout(cartItems);
  }

  /**
   * Show loading state on checkout button
   */
  #showLoadingState() {
    if (!this.refs.checkoutButton || !this.refs.checkoutLoader) return;

    const text = this.refs.checkoutButton.querySelector('[data-checkout-text]');
    
    this.refs.checkoutButton.classList.add('is-loading');
    this.refs.checkoutButton.setAttribute('disabled', 'disabled');
    
    const loaderEl = this.refs.checkoutLoader;
    if (loaderEl && loaderEl instanceof HTMLElement) {
      loaderEl.style.display = 'inline-flex';
    }
    
    if (text && text instanceof HTMLElement) {
      text.style.visibility = 'hidden';
    }

    // Sync loading state to sticky checkout button
    if (this.refs.stickyCheckoutButton && this.refs.stickyCheckoutLoader && 
        this.refs.stickyCheckoutButton instanceof HTMLElement) {
      const stickyText = this.refs.stickyCheckoutButton.querySelector('[data-sticky-checkout-text]');
      
      this.refs.stickyCheckoutButton.classList.add('is-loading');
      this.refs.stickyCheckoutButton.setAttribute('disabled', 'disabled');
      
      const stickyLoaderEl = this.refs.stickyCheckoutLoader;
      if (stickyLoaderEl && stickyLoaderEl instanceof HTMLElement) {
        stickyLoaderEl.style.display = 'inline-flex';
      }
      
      if (stickyText && stickyText instanceof HTMLElement) {
        stickyText.style.visibility = 'hidden';
      }
    }
  }

  /**
   * Hide loading state on checkout button
   */
  #hideLoadingState() {
    if (!this.refs.checkoutButton || !this.refs.checkoutLoader) return;

    const text = this.refs.checkoutButton.querySelector('[data-checkout-text]');
    
    this.refs.checkoutButton.classList.remove('is-loading');
    this.refs.checkoutButton.removeAttribute('disabled');
    
    const loaderEl = this.refs.checkoutLoader;
    if (loaderEl && loaderEl instanceof HTMLElement) {
      loaderEl.style.display = 'none';
    }
    
    if (text && text instanceof HTMLElement) {
      text.style.visibility = 'visible';
    }

    // Sync loading state to sticky checkout button
    if (this.refs.stickyCheckoutButton && this.refs.stickyCheckoutLoader && 
        this.refs.stickyCheckoutButton instanceof HTMLElement) {
      const stickyText = this.refs.stickyCheckoutButton.querySelector('[data-sticky-checkout-text]');
      
      this.refs.stickyCheckoutButton.classList.remove('is-loading');
      this.refs.stickyCheckoutButton.removeAttribute('disabled');
      
      const stickyLoaderEl = this.refs.stickyCheckoutLoader;
      if (stickyLoaderEl && stickyLoaderEl instanceof HTMLElement) {
        stickyLoaderEl.style.display = 'none';
      }
      
      if (stickyText && stickyText instanceof HTMLElement) {
        stickyText.style.visibility = 'visible';
      }
    }
  }

  /**
   * Redirect to checkout with cart items
   * @param {{id: string, quantity: number, selling_plan?: string}[]} cartItems - Cart items to add
   */
  #redirectToCheckout(cartItems) {
    console.log('🔄 Starting checkout redirect process...');
    console.log('📦 Cart items to add:', cartItems);
    
    // Validate cart items
    if (!cartItems || cartItems.length === 0) {
      console.error('❌ No cart items to add');
      alert('Error: No items selected. Please select at least one item.');
      this.#hideLoadingState();
      return;
    }
    
    // Validate each item has required fields
    const invalidItems = cartItems.filter(item => !item.id || !item.quantity || item.quantity <= 0);
    if (invalidItems.length > 0) {
      console.error('❌ Invalid cart items:', invalidItems);
      alert('Error: Some items are invalid. Please try again.');
      this.#hideLoadingState();
      return;
    }
    
    console.log('🧹 Clearing cart first...');
    
    // Clear cart first
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => {
      console.log('✅ Cart cleared, status:', response.status);
      if (!response.ok) {
        throw new Error(`Cart clear failed with status: ${response.status}`);
      }
      return response.json();
    })
    .then(() => {
      console.log('📦 Adding items to cart via API...');
      
      // Use Cart API to add items properly
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: cartItems
        })
      });
    })
    .then(response => {
      console.log('📊 Add to cart response status:', response.status);
      if (!response.ok) {
        return response.text().then(text => {
          throw new Error(`Failed to add items to cart: ${response.status} - ${text}`);
        });
      }
      return response.json();
    })
    .then(cartData => {
      console.log('✅ Items added to cart successfully:', cartData);
      console.log('🔗 Redirecting to checkout...');
      
      // Redirect to checkout with discount code if available
      if (this.#discountCode) {
        const checkoutUrl = `/discount/${this.#discountCode}?redirect=/checkout`;
        console.log('💰 Using discount redirect:', this.#discountCode);
        console.log('🔗 Final URL:', checkoutUrl);
        window.location.href = checkoutUrl;
      } else {
        console.log('🔗 Redirecting directly to checkout');
        window.location.href = '/checkout';
      }
    })
    .catch(error => {
      console.error('❌ Error during checkout process:', error);
      alert('Error adding items to cart. Please try again.');
      this.#hideLoadingState();
    });
  }

  /**
   * Normalize a variant id to numeric id if a Shopify GID is provided
   * @param {string} id
   * @returns {string}
   */
  #normalizeVariantId(id) {
    if (typeof id === 'string' && id.startsWith('gid://')) {
      const parts = id.split('/');
      return parts[parts.length - 1] || '';
    }
    return id;
  }

  /**
   * Update all pricing displays based on current selection
   */
  #updateAllPricingDisplays() {
    // Update subscription pricing
    this.#updateSubscriptionPricing();
    
    // Update select colors button pricing
    this.#updateSelectColorsPricing();
    
    // Brand text is now static - no dynamic updates needed
    
    // Sync pricing to sticky button
    this.#syncStickyButtonPricing();
  }

  /**
   * Update free gifts visual unlock state based on quantity and subscription.
   */
  #updateGiftUnlocks() {
    const unlocked = this.#getUnlockedGiftKeys();

    const giftCards = this.querySelectorAll('.bundle-save__gift-card');
    giftCards.forEach(card => {
      const key = card.getAttribute('data-gift-key');
      const image = card.querySelector('.bundle-save__gift-image');
      if (!image) return;
      
      if (unlocked.has(key || '')) {
        image.classList.add('is-unlocked');
      } else {
        image.classList.remove('is-unlocked');
        // Update unlock text based on gift requirements
        this.#updateGiftUnlockText(card, key || '');
      }
    });
  }

  /**
   * Update the unlock text for a specific gift card
   * @param {Element} card - The gift card element
   * @param {string} giftKey - The gift key (ebook, shipping, bottle, mystery)
   */
  #updateGiftUnlockText(card, giftKey) {
    const unlockTextElement = card.querySelector('.bundle-save__gift-unlock-text');
    if (!unlockTextElement) return;

    let unlockText = '';
    
    // New unlock requirements: ebook at 3, all others at 6
    switch (giftKey) {
      case 'ebook':
        unlockText = '3+ Cans';
        break;
      case 'shipping':
      case 'bottle':
      case 'mystery':
        unlockText = '6+ Cans';
        break;
      default:
        unlockText = '3+ Cans';
    }

    unlockTextElement.textContent = unlockText;
  }

  /**
   * Get the effective quantity for determining gift unlocks.
   * Uses the larger of selected quantity (minimum tier) and total variant selections (can unlock higher tier).
   * @returns {number}
   */
  #getEffectiveQuantityForGifts() {
    const selectedQty = this.#selectedQuantity;
    const variantsQty = this.#getTotalQuantity();
    return Math.max(selectedQty, variantsQty);
  }

  /**
   * Compute unlocked gift keys according to business rules
   * @returns {Set<string>} keys among: ebook, shipping, bottle, mystery
   */
  #getUnlockedGiftKeys() {
    const quantity = this.#getEffectiveQuantityForGifts();
    const unlocked = new Set();

    // Ebook unlocks at 3 cans
    if (quantity >= 3) {
      unlocked.add('ebook');
    }

    // All 4 gifts unlock at 6 cans (ebook, shipping, bottle, mystery)
    if (quantity >= 6) {
      unlocked.add('shipping');
      unlocked.add('bottle');
      unlocked.add('mystery');
    }

    return unlocked;
  }

  /**
   * Update subscription pricing display
   */
  #updateSubscriptionPricing() {
    const priceComparisons = this.querySelectorAll('[data-price-comparison]');
    
    priceComparisons.forEach((el) => {
      const priceComparison = /** @type {HTMLElement} */ (el);
      const originalPrice = priceComparison.querySelector('.bundle-save__original-price');
      const discountedPrice = priceComparison.querySelector('.bundle-save__discounted-price');
      
      if (originalPrice && discountedPrice) {
        if (priceComparison.dataset.priceComparison === 'subscription') {
          // Subscription pricing - use the selected quantity price as base
          const basePrice = this.#getQuantityPrice(this.#selectedQuantity);
          const discountMultiplier = (100 - this.#config.subscriptionDiscountPercent) / 100;
          const subscriptionPrice = basePrice * discountMultiplier;
          discountedPrice.textContent = `$${subscriptionPrice.toFixed(2)}/ea`;
        } else if (priceComparison.dataset.priceComparison === 'one-time') {
          // One-time pricing
          const quantityPrice = this.#getQuantityPrice(this.#selectedQuantity);
          discountedPrice.textContent = `$${quantityPrice.toFixed(2)}`;
          
          // Show original price for comparison
          originalPrice.classList.remove('hidden');
        }
      }
    });
  }

  /**
   * Update select colors button pricing
   */
  #updateSelectColorsPricing() {
    const priceComparison = this.querySelector('[data-price-comparison]:not([data-price-comparison="subscription"]):not([data-price-comparison="one-time"])');
    if (!priceComparison) return;

    const originalPrice = priceComparison.querySelector('.bundle-save__original-price');
    const discountedPrice = priceComparison.querySelector('.bundle-save__discounted-price');
    
    if (originalPrice && discountedPrice) {
      // Use the max bag price (from config or actual product price) as the "original" price for comparison
      const highestPrice = this.#config.productPriceMin > 0 
        ? Math.max(this.#config.productPriceMin, this.#config.pricing.maxBagPrice)
        : this.#config.pricing.maxBagPrice;
      const originalTotal = highestPrice * this.#selectedQuantity;
      const quantityPrice = this.#getQuantityPrice(this.#selectedQuantity);
      const discountedTotal = quantityPrice * this.#selectedQuantity;
      
      originalPrice.textContent = `$${originalTotal.toFixed(2)}`;
      discountedPrice.textContent = `$${discountedTotal.toFixed(2)}`;
      
      // Always show original price for comparison since there are quantity discounts
      originalPrice.classList.remove('hidden');
    }
  }


  /**
   * Get price for a specific quantity
   * Uses actual product variant prices if available, otherwise falls back to configured prices
   * @param {number} quantity - The quantity
   * @returns {number} The price per unit in dollars
   */
  #getQuantityPrice(quantity) {
    // Try to get actual price from product variants
    if (this.#config.productVariants && this.#config.productVariants.length > 0) {
      // Get the first available variant price (in cents, convert to dollars)
      const firstVariant = this.#config.productVariants[0];
      if (!firstVariant || !firstVariant.price) {
        // Fallback to configured prices if variant data is invalid
        if (quantity >= 3) return this.#config.pricing.quantity3Price;
        if (quantity === 2) return this.#config.pricing.quantity2Price;
        return this.#config.pricing.quantity1Price;
      }
      const variantPrice = firstVariant.price / 100;
      
      // Apply quantity-based pricing if we have multiple variants or quantity rules
      // For now, use the base variant price and apply quantity discounts
      // This assumes the product has quantity breaks configured in Shopify
      if (quantity >= 3) {
        // Check if we have a variant or price for quantity 3, otherwise use fallback
        return variantPrice > 0 ? Math.min(variantPrice, this.#config.pricing.quantity3Price) : this.#config.pricing.quantity3Price;
      } else if (quantity === 2) {
        return variantPrice > 0 ? Math.min(variantPrice, this.#config.pricing.quantity2Price) : this.#config.pricing.quantity2Price;
      } else {
        return variantPrice > 0 ? Math.max(variantPrice, this.#config.pricing.quantity1Price) : this.#config.pricing.quantity1Price;
      }
    }
    
    // Fallback to configured prices
    if (quantity >= 3) {
      return this.#config.pricing.quantity3Price;
    } else if (quantity === 2) {
      return this.#config.pricing.quantity2Price;
    } else {
      return this.#config.pricing.quantity1Price;
    }
  }

  /**
   * Hide all page content except countdown bar and gallery (desktop only) for color selection
   */
  #hidePageContentForColorSelection() {
    // Store original display states for restoration
    this.#originalDisplayStates = new Map();
    
    // Get main content area
    const mainContent = document.getElementById('MainContent');
    if (!mainContent) return;
    
    // Hide all sections except countdown bar, gallery (desktop only), and bundle-save
    const sections = mainContent.querySelectorAll('.shopify-section, .section-wrapper');
    console.log('Found sections:', sections.length);
    sections.forEach(section => {
      if (!(section instanceof HTMLElement)) return;
      
      const sectionId = section.id || section.className;
      console.log('Processing section:', sectionId);
      
      // Keep countdown bar visible
      if (section.querySelector('.countdown-bar')) {
        console.log('Keeping countdown bar visible:', sectionId);
        return;
      }
      
      // Keep gallery visible on desktop only
      const hasGallery = section.querySelector('.product-gallery, .media-gallery, .gallery');
      if (hasGallery && this.#isDesktop()) {
        console.log('Keeping gallery visible on desktop:', sectionId);
        return;
      }
      
      // Keep product-display__media visible on desktop only
      const hasProductDisplayMedia = section.querySelector('.product-display__media');
      if (hasProductDisplayMedia) {
        if (this.#isDesktop()) {
          console.log('Keeping product-display__media visible on desktop:', sectionId);
          return; // Keep visible on desktop
        } else {
          console.log('Hiding product-display__media on mobile:', sectionId);
          // Don't return, let it be hidden on mobile
        }
      }
      
      // Keep bundle-save section visible (both desktop and mobile)
      if (section.querySelector('.bundle-save-block')) {
        console.log('Keeping bundle-save-block visible:', sectionId);
        return;
      }
      
      // Keep any section that contains bundle-save-block visible (both desktop and mobile)
      if (section.querySelector('[id*="bundle_save"]')) {
        console.log('Keeping section with bundle_save ID visible:', sectionId);
        return;
      }
      
      // Keep any section that contains bundle-save-component visible (both desktop and mobile)
      if (section.querySelector('bundle-save-component')) {
        console.log('Keeping section with bundle-save-component visible:', sectionId);
        return;
      }
      
      // Keep any section that contains shopify-block with bundle-save-block class visible (both desktop and mobile)
      if (section.querySelector('.shopify-block.bundle-save-block')) {
        console.log('Keeping section with shopify-block.bundle-save-block visible:', sectionId);
        return;
      }
      
      // Store original display state and hide section
      console.log('Hiding section:', sectionId);
      if (this.#originalDisplayStates) {
        this.#originalDisplayStates.set(section, section.style.display || '');
      }
      section.style.display = 'none';
    });
    
    // Hide content blocks except those in protected areas
    const contentBlocks = mainContent.querySelectorAll('.group-block, .shopify-block');
    console.log('Found content blocks:', contentBlocks.length);
    contentBlocks.forEach(block => {
      if (!(block instanceof HTMLElement)) return;
      
      const blockId = block.id || block.className;
      console.log('Processing content block:', blockId);
      
      // Skip blocks inside bundle-save
      if (block.contains(document.querySelector('.bundle-save-block'))) {
        console.log('Skipping block inside bundle-save:', blockId);
        return;
      }
      
      // Skip bundle-save shopify-block elements
      if (block.classList.contains('shopify-block') && block.classList.contains('bundle-save-block')) {
        console.log('Skipping bundle-save shopify-block:', blockId);
        return;
      }
      
      // Skip blocks inside countdown bar
      if (block.closest('.countdown-bar')) {
        console.log('Skipping block inside countdown bar:', blockId);
        return;
      }
      
      // Skip blocks inside gallery (desktop only)
      const isInGallery = block.closest('.product-gallery, .media-gallery, .gallery');
      if (isInGallery && this.#isDesktop()) {
        console.log('Skipping block inside gallery on desktop:', blockId);
        return;
      }
      
      // Skip blocks inside product-display__media (desktop only)
      const isInProductDisplayMedia = block.closest('.product-display__media');
      if (isInProductDisplayMedia) {
        if (this.#isDesktop()) {
          console.log('Skipping block inside product-display__media on desktop:', blockId);
          return; // Keep visible on desktop
        } else {
          console.log('Hiding block inside product-display__media on mobile:', blockId);
          // Don't return, let it be hidden on mobile
        }
      }
      
      // Store original display state and hide block
      console.log('Hiding content block:', blockId);
      if (this.#originalDisplayStates) {
        this.#originalDisplayStates.set(block, block.style.display || '');
      }
      block.style.display = 'none';
    });
  }
  
  /**
   * Restore all page content from color selection mode
   */
  #restorePageContentFromColorSelection() {
    if (!this.#originalDisplayStates) return;
    
    // Restore all stored display states
    this.#originalDisplayStates.forEach((originalDisplay, element) => {
      if (element && element instanceof HTMLElement) {
        element.style.display = originalDisplay;
      }
    });
    
    // Clear the stored states
    this.#originalDisplayStates.clear();
  }
  
  /**
   * Check if the current viewport is desktop size
   * @returns {boolean} True if desktop, false if mobile
   */
  #isDesktop() {
    return window.innerWidth >= 768;
  }



  /**
   * Test discount code application (for debugging)
   * This method can be called from the browser console to test discount code functionality
   */
  testDiscountCode() {
    console.log('🧪 Testing discount code application...');
    console.log('💰 Current discount code:', this.#discountCode);
    
    // Test URL encoding
    const testCode = 'WELCOME';
    const encoded = encodeURIComponent(testCode);
    console.log('🔤 Test encoding:', testCode, '->', encoded);
    
    // Test URL building
    const testUrl = '/cart/add?items[0][id]=123&items[0][quantity]=1&return_to=/checkout';
    const urlWithDiscount = `${testUrl}&discount=${encoded}`;
    console.log('🔗 Test URL with discount:', urlWithDiscount);
    
    // Test Safari-compatible URL format
    const safariDiscountUrl = `/discount/${testCode}?redirect=${encodeURIComponent(testUrl)}`;
    console.log('🍎 Safari-compatible discount URL:', safariDiscountUrl);
    
    // Test URL parsing
    try {
      const urlObj = new URL(urlWithDiscount, window.location.origin);
      const discountParam = urlObj.searchParams.get('discount');
      console.log('🔍 Parsed discount param:', discountParam);
      
      if (discountParam === testCode) {
        console.log('✅ Discount code encoding/decoding works correctly');
      } else {
        console.error('❌ Discount code encoding/decoding failed');
      }
    } catch (error) {
      console.error('❌ URL parsing error:', error);
    }
    
    // Test Safari-specific issues
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isSafari || isIOS) {
      console.log('🍎 Safari/iOS detected - testing specific compatibility');
      
      // Test fetch API
      console.log('🔧 Testing fetch API availability...');
      if (typeof fetch !== 'undefined') {
        console.log('✅ Fetch API available');
      } else {
        console.error('❌ Fetch API not available');
      }
      
      // Test Promise API
      console.log('🔧 Testing Promise API availability...');
      if (typeof Promise !== 'undefined') {
        console.log('✅ Promise API available');
      } else {
        console.error('❌ Promise API not available');
      }
      
      // Test URL constructor
      console.log('🔧 Testing URL constructor availability...');
      if (typeof URL !== 'undefined') {
        console.log('✅ URL constructor available');
      } else {
        console.error('❌ URL constructor not available');
      }
    }
    
    return {
      discountCode: this.#discountCode,
      encoded: encoded,
      testUrl: urlWithDiscount,
      safariDiscountUrl: safariDiscountUrl,
      isSafari: isSafari,
      isIOS: isIOS
    };
  }

  /**
   * Handle form submission
   * @param {Event} event - The submit event
   */
  handleSubmit(event) {
    event.preventDefault();
    // Handle form submission if needed
  }
}

// Function to update "Order Today" text to include today's date
function updateOrderTodayText() {
  // Find the element with the specific class
  const textElement = document.querySelector('.text-block--AQlZCM2hnUS95UWZ4V__text_F86Ymg');
  
  if (textElement) {
    // Get today's date and format it
    const today = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const day = today.getDate();
    const month = monthNames[today.getMonth()];
    
    // Add ordinal suffix to day
    let dayWithSuffix;
    if (day >= 11 && day <= 13) {
      dayWithSuffix = day + 'th';
    } else {
      switch (day % 10) {
        case 1: dayWithSuffix = day + 'st'; break;
        case 2: dayWithSuffix = day + 'nd'; break;
        case 3: dayWithSuffix = day + 'rd'; break;
        default: dayWithSuffix = day + 'th';
      }
    }
    
    const todayFormatted = `${month} ${dayWithSuffix}`;
    
    // Update the text content
    const paragraph = textElement.querySelector('p');
    if (paragraph) {
      paragraph.innerHTML = `<strong>Order By ${todayFormatted} For Guaranteed Free Gifts</strong>`;
    }
  }
}

// Run the update when the DOM is loaded
document.addEventListener('DOMContentLoaded', updateOrderTodayText);

// Also run it immediately in case the DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateOrderTodayText);
} else {
  updateOrderTodayText();
}

// Register the custom element
customElements.define('bundle-save-component', BundleSaveComponent);

// Global debug functions for Safari testing
if (typeof window !== 'undefined') {
  /** @type {any} */ (window).debugBundleSave = () => {
    console.log('🔍 Debugging Bundle Save Component...');
    const component = /** @type {BundleSaveComponent} */ (document.querySelector('bundle-save-component'));
    if (component && component.testDiscountCode) {
      return component.testDiscountCode();
    } else {
      console.error('❌ Bundle save component not found or testDiscountCode method not available');
      return null;
    }
  };
  
  
  /** @type {any} */ (window).testSafariDiscount = () => {
    console.log('🍎 Testing Safari discount code functionality...');
    console.log('🌐 User Agent:', navigator.userAgent);
    console.log('🍎 Is Safari:', /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent));
    console.log('📱 Is iOS:', /iPad|iPhone|iPod/.test(navigator.userAgent));
    
    // Test basic URL encoding
    const testCode = 'WELCOME';
    const encoded = encodeURIComponent(testCode);
    console.log('🔤 Encoding test:', testCode, '->', encoded);
    
    // Test URL building
    const baseUrl = '/cart/add?items[0][id]=123&items[0][quantity]=1&return_to=/checkout';
    const fullUrl = `${baseUrl}&discount=${encoded}`;
    console.log('🔗 Full URL:', fullUrl);
    
    // Test Safari-compatible URL format
    const safariDiscountUrl = `/discount/${testCode}?redirect=${encodeURIComponent(baseUrl)}`;
    console.log('🍎 Safari-compatible discount URL:', safariDiscountUrl);
    
    // Test URL parsing
    try {
      const url = new URL(fullUrl, window.location.origin);
      const discount = url.searchParams.get('discount');
      console.log('🔍 Parsed discount:', discount);
      console.log('✅ URL parsing successful');
    } catch (error) {
      console.error('❌ URL parsing failed:', error);
    }
    
    return {
      userAgent: navigator.userAgent,
      isSafari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
      testCode: testCode,
      encoded: encoded,
      fullUrl: fullUrl,
      safariDiscountUrl: safariDiscountUrl
    };
  };
  
  console.log('🧪 Debug functions available:');
  console.log('  - window.debugBundleSave() - Test the bundle save component');
  console.log('  - window.testSafariDiscount() - Test Safari discount functionality');
}